import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { useToast } from "@/hooks/use-toast";
import { Save, Eye, EyeOff, ExternalLink } from "lucide-react";

interface SponsorData {
  name: string | null;
  logoUrl: string | null;
  websiteUrl: string | null;
  active: boolean;
}

function authHeaders() {
  const token = localStorage.getItem("ayrshire_token");
  return { "Content-Type": "application/json", ...(token ? { Authorization: `Bearer ${token}` } : {}) };
}

export default function AdminSponsor() {
  const { toast } = useToast();
  const [data, setData] = useState<SponsorData>({ name: "", logoUrl: "", websiteUrl: "", active: false });
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    fetch("/api/sponsor")
      .then(r => r.json())
      .then((d: SponsorData) => setData({ name: d.name ?? "", logoUrl: d.logoUrl ?? "", websiteUrl: d.websiteUrl ?? "", active: d.active }))
      .finally(() => setLoading(false));
  }, []);

  const handleSave = async () => {
    setSaving(true);
    try {
      const res = await fetch("/api/admin/sponsor", {
        method: "PATCH",
        headers: authHeaders(),
        body: JSON.stringify({ name: data.name, logoUrl: data.logoUrl, websiteUrl: data.websiteUrl, active: data.active }),
      });
      if (!res.ok) throw new Error("Failed to save");
      toast({ title: "Sponsor updated", description: "Changes are live immediately." });
    } catch {
      toast({ title: "Error", description: "Could not save sponsor settings.", variant: "destructive" });
    } finally {
      setSaving(false);
    }
  };

  if (loading) return <AdminLayout current="/admin/sponsor"><div className="p-8 text-slate-400">Loading…</div></AdminLayout>;

  return (
    <AdminLayout current="/admin/sponsor">
      <div className="max-w-2xl mx-auto p-6 space-y-6">
        <div>
          <h1 className="text-2xl font-bold text-foreground">App Sponsor</h1>
          <p className="text-slate-500 mt-1 text-sm">
            Configure the business currently sponsoring Ayrshire Hub. Their logo and name appear as a subtle banner in the app.
          </p>
        </div>

        {/* Preview */}
        {data.active && data.name && (
          <div className="border border-amber-200 bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4">
            <p className="text-xs font-semibold text-slate-400 uppercase tracking-wide mb-2">Preview</p>
            <div className="flex items-center gap-3 bg-white rounded-lg px-4 py-3 border border-slate-100 shadow-sm">
              {data.logoUrl ? (
                <img src={data.logoUrl} alt={data.name} className="h-8 w-auto max-w-[100px] object-contain" />
              ) : (
                <div className="h-8 w-8 rounded bg-slate-100 flex items-center justify-center text-slate-400 text-xs">Logo</div>
              )}
              <div className="flex-1 min-w-0">
                <p className="text-[10px] text-slate-400 font-medium uppercase tracking-wide">Sponsored by</p>
                <p className="text-sm font-semibold text-slate-800 truncate">{data.name}</p>
              </div>
              <ExternalLink size={14} className="text-slate-400 shrink-0" />
            </div>
          </div>
        )}

        {/* Active toggle */}
        <div className="border border-slate-200 rounded-xl p-5 space-y-4 bg-white">
          <div className="flex items-center justify-between">
            <div>
              <p className="font-semibold text-slate-800">Show sponsor banner</p>
              <p className="text-sm text-slate-500">When enabled, the banner appears on the home screen of the app and website.</p>
            </div>
            <button
              onClick={() => setData(d => ({ ...d, active: !d.active }))}
              className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors ${data.active ? "bg-primary" : "bg-slate-200"}`}
            >
              <span className={`inline-block h-4 w-4 rounded-full bg-white shadow transition-transform ${data.active ? "translate-x-6" : "translate-x-1"}`} />
            </button>
          </div>
        </div>

        {/* Fields */}
        <div className="border border-slate-200 rounded-xl p-5 space-y-5 bg-white">
          <h2 className="font-semibold text-slate-800">Sponsor details</h2>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">Company name</label>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={data.name ?? ""}
              onChange={e => setData(d => ({ ...d, name: e.target.value }))}
              placeholder="e.g. Kilmarnock FC"
            />
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">Logo URL</label>
            <p className="text-xs text-slate-400">Direct link to the sponsor's logo image. Use a transparent PNG for best results.</p>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={data.logoUrl ?? ""}
              onChange={e => setData(d => ({ ...d, logoUrl: e.target.value }))}
              placeholder="https://example.com/logo.png"
            />
            {data.logoUrl && (
              <div className="mt-2 p-2 bg-slate-50 rounded-lg inline-block">
                <img src={data.logoUrl} alt="Logo preview" className="h-10 max-w-[160px] object-contain" onError={e => (e.currentTarget.style.display = "none")} />
              </div>
            )}
          </div>

          <div className="space-y-1">
            <label className="text-sm font-medium text-slate-700">Website URL</label>
            <p className="text-xs text-slate-400">Where tapping the banner takes the user. Must include https://</p>
            <input
              className="w-full border border-slate-200 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-primary/20"
              value={data.websiteUrl ?? ""}
              onChange={e => setData(d => ({ ...d, websiteUrl: e.target.value }))}
              placeholder="https://sponsor.example.com"
            />
          </div>
        </div>

        <div className="flex justify-end">
          <button
            onClick={handleSave}
            disabled={saving}
            className="flex items-center gap-2 bg-primary text-white px-6 py-2 rounded-lg text-sm font-semibold hover:bg-primary/90 transition disabled:opacity-60"
          >
            <Save size={15} />
            {saving ? "Saving…" : "Save changes"}
          </button>
        </div>
      </div>
    </AdminLayout>
  );
}
